// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { DeleteTagKeywordRequest } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import { notUndefined } from "lib/utils"
import { grpc } from "../../../app/rpc/executor"
import { tagServiceClient } from "../../../app/rpc/client"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { TagKeyword } from "../../../generated/legebill/type/tag_pb"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeleteTagKeyword = (request: DeleteTagKeywordRequest) => Observable<Empty>

export const deleteTagKeyword = (deleteTagKeywordRpc: DeleteTagKeyword) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTagKeyword.match),
      mergeMap(({ payload }) => of(payload)
        .pipe(
          mergeMap(deleteTagKeywordRpc),
          map(_ => payload.getKeyword()?.toObject()),
          filter(notUndefined),
          map(Slice.deleteTagKeywordFulfilled),
          catchError(e => handleError(e, payload.getKeyword())),
        )
      )
    )

const deleteTagKeywordRpc = (request: DeleteTagKeywordRequest): Observable<Empty> =>
  grpc(() => tagServiceClient.deleteTagKeyword(request, null))

const handleError = (e: unknown, tagKeyword: TagKeyword | undefined): Observable<Action> => {
  if (isGrpcError(e) && tagKeyword !== undefined) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_delete_tag_keyword_not_found", message: "Tag not found. Unable to delete the keyword." }),
          messageType: "ERROR",
        })),
        of(Slice.setDialogStatus("CLOSED")),
        of(Slice.removeFromCache(tagKeyword.getTagId()))
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_delete_tag_keyword_permission_denied", message: "Failed to delete keyword. You do not have permission to edit tags." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_delete_tag_keyword_failed", message: "Failed to delete keyword. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default deleteTagKeyword(deleteTagKeywordRpc)
